/**
 * RACE WEEK — the whole week, not a form.
 *
 * "Every race has to have a qualifying event, right? Rather than roll dice for
 *  50-60 drivers, RED WHITE & BLUE RACIN's 'Race Week' uses a system that
 *  quickly approximates each driver's race week results by shuffling the driver
 *  cards and dealing each card randomly into one of four categories…"
 *                                                            — RWEG/p.2
 *
 * TWO WAYS IN, ADDED 17 Aug 2026. Howard: *"I think starting a season option
 * ought to be on the race week creator as well as an option and lead into race
 * #1 no… the racetracks are controlled by the season (prescheduled each year)
 * so the user wouldnt pick the track upon career creation."* So the entry
 * screen now opens on a mode switch:
 *
 *   • SEASON     — `buildSchedule(seed)` owns the calendar. The track select is
 *                  not disabled, it is GONE, and in its place is the fact:
 *                  race 1 of 36, this event, at this track, over this distance.
 *   • EXHIBITION — the player owns the track. One race, for its own sake.
 *
 * Which of those is in force is `planEntry` in `entryMode.ts`, tested there.
 *
 * A SEASON DOES NOT RUN HERE, AND HAS NOT SINCE `seasonPlay.ts` LANDED. Season
 * mode on this screen is a starting gate: the seed, the roster and the field
 * size, and one button that hands them to the shell. The shell calls
 * `beginSeason` and renders `SeasonPlay.tsx` in this screen's place, which then
 * plays races 1 through 36 one at a time off a `SeasonState` the shell owns.
 * The three stages below are therefore the EXHIBITION path only.
 *
 * ⚠ WHAT USED TO BE HERE: a panel headed "This plays the opener, and then
 * stops", explaining at length that races 2-36 were not playable. They are.
 * Deleted, along with the constant behind it — see the note in `entryMode.ts`.
 *
 * Three screens, in the order the booklet works:
 *
 *   1  ENTRY LIST     the track, the distance, the seed, and the LICENSED
 *                     ROSTER — 53 men who turn up, 43 of whom will start.
 *   2  RACE WEEK      steps one to five, revealed as they resolve: the TV list
 *                     with every chart chain and its dice, the interviews, the
 *                     three stacks, and every consequence the week generated.
 *   3  THE GRID       step six — the 43 who made it, plus the men who failed to
 *                     qualify and the men left on the bubble.
 *
 * NOTHING HERE IS A RULE. `runRaceWeek` is called once, with the seed and the
 * roster, and everything on all three screens is read off the result it
 * returned. The screen cannot roll a die, break a tie or place a card.
 *
 * D-EZ-103 — EXHIBITIONS ONLY NOW. A season starts in exactly one place, league
 * creation (`LeagueCreate.tsx`), and is played from the season home
 * (`SeasonPlay.tsx`). This screen's old "Season" mode — a second season start
 * with a bare seed field — is gone. What it gained: the realism house rules
 * (D-EZ-83…87) under Advanced, and `startAtWeek` so "Run another race here"
 * goes straight to race week.
 */

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { buildField, DEFAULT_ROSTER_SIZE } from '../domain/field.ts';
import type { Team } from '../domain/field.ts';
import { MAX_STARTING_FIELD, runRaceWeek } from '../domain/raceWeek.ts';
import type { RaceWeekResult } from '../domain/raceWeek.ts';
import { TRACK_CARDS, trackCard } from '../domain/turn.ts';
import type { TrackCard } from '../domain/turn.ts';
import type { DriverCard } from '../domain/types.ts';
import { liveryForDriver } from '../domain/livery.ts';
import type { Livery } from '../domain/livery.ts';
import { weekStory } from './raceWeekModel.ts';
import { feudHistories } from '../domain/feudHistory.ts';
import WeekReveal from './WeekReveal.tsx';
import StartingGrid, { DriverTile } from './StartingGrid.tsx';
import DriverDossier from './DriverDossier.tsx';
import RaceLogo from './RaceLogo.tsx';
import { raceAccentProps, useRaceAccent } from './raceAccent.ts';
import { planEntry, type ScenarioId } from './entryMode.ts';
import { parseSeed, randomSeed } from '../lib/seed.ts';
import { Cite, useShowCitations } from './Cite.tsx';
import type { HouseRuleOptions } from '../domain/houseRules.ts';
import { RealismRules } from './LeagueSettingsForm.tsx';
import { settingsSummary } from '../lib/leagueSettings.ts';

export interface RaceConfig {
  trackId: string;
  totalTurns: number;
  seed: number;
  /** The starting field RWEG/p.4 STEP SIX rounds out to. Never above 43. */
  fieldSize: number;
  /** How many drivers hold a licence and turn up to try. */
  rosterSize: number;
  /**
   * The EVENT's name, when the calendar named it.
   *
   * A track card is a venue and a named race is an event (N-005): fourteen
   * cards print a second race, so "charlotte-2014" is the Coca-Cola 600 AND the
   * Bank of America 500. Without this the race would be titled off the card and
   * a scheduled 500 would run under the 600's name.
   */
  raceName?: string;
  /**
   * Season mode only — which race of the 36 this is. Absent for an exhibition,
   * which is not race anything of anything. The finance fold reads it: gate
   * attendance and the season's test races both move with the race number.
   */
  seasonRaceNumber?: number;
  /**
   * D-018 — roll your own dice.
   *
   * When true the race is not resolved on the seed at all: the engine runs and
   * STOPS at every single die it needs, and Howard reads the face off his own
   * table. The seed still matters (it deals the field, and it is what the
   * "let the engine finish the rest" escape hatch falls back to), but the race
   * itself is his.
   *
   * It is a property of THIS RACE rather than a setting, because it is a
   * decision about how one evening is going to be spent — roughly 900 dice for
   * a 50-turn race — and not a preference to be left switched on by accident.
   */
  handRolled?: boolean;
  /**
   * D-EZ-103 — the realism house rules for THIS exhibition (D-EZ-83…87). Absent
   * = all on, modern era — the same default a league starts with.
   */
  houseRules?: HouseRuleOptions;
}

/** What season mode hands the shell when the player starts a career. */
export interface SeasonStart {
  seed: number;
  /** Everyone who holds a licence. The season's whole population. */
  rosterSize: number;
  /** RWEG/p.4 STEP SIX — how many of them line up each Sunday. */
  fieldSize: number;
  /**
   * D-EZ-76 — a printed year to start the career from, or null for a dealt one.
   *
   * The shell reads this and builds the roster, the charter and the calendar
   * from `scenario2025.ts` instead of `buildField`. Nothing after race 1 knows
   * the difference. `rosterSize` is IGNORED when it is set — the scenario has
   * the seats it has, and 45 of them is not a preference.
   */
  scenario?: ScenarioId | null;
}

export interface RaceSetupProps {
  initial: RaceConfig;
  /** Hand the week — grid, TV list, feuds, fume list, chips — to the race. */
  onStart: (config: RaceConfig, week: RaceWeekResult) => void;
  /** D-EZ-103 — roll race week as soon as the screen opens ("Run another race here"). */
  startAtWeek?: boolean;
  /** D-EZ-103 — back to the front door. */
  onBack?: () => void;
  /** Shown above the form (the name-pool line). */
  note?: ReactNode;
}

type Stage = 'entry' | 'week' | 'grid';
type Pace = 'instant' | 'stepped';

const ROSTER_SIZES = [53, 48, 43, 38] as const;
const FIELD_SIZES = [43, 38, 33, 28] as const;
const LENGTHS = [20, 22, 28, 32, 40, 50, 60] as const;

/** How long one beat of the reveal holds the screen before the next lands. */
const BEAT_MS = 620;

const CATEGORY_ORDER = ['Super Speedway', 'Intermediate Track', 'Short Track', 'Road Course'];

function byCategory(cards: readonly TrackCard[]) {
  const groups = new Map<string, TrackCard[]>();
  for (const card of cards) {
    const list = groups.get(card.category) ?? [];
    list.push(card);
    groups.set(card.category, list);
  }
  const rank = (category: string) => {
    const i = CATEGORY_ORDER.indexOf(category);
    return i === -1 ? CATEGORY_ORDER.length : i;
  };
  return [...groups.entries()].sort((a, b) => rank(a[0]) - rank(b[0]));
}

export default function RaceSetup({ initial, onStart, startAtWeek = false, onBack, note }: RaceSetupProps) {
  const [stage, setStage] = useState<Stage>('entry');
  /** D-EZ-103 — this screen is the exhibition, always. */
  const mode = 'exhibition' as const;
  const [trackId, setTrackId] = useState(initial.trackId);
  const citations = useShowCitations();
  const [seed, setSeed] = useState(initial.seed);
  /**
   * The seed field's TEXT, kept beside the seed itself.
   *
   * A seed is a name, not a quantity (see `lib/seed.ts`), so the field is typed
   * rather than nudged — and a half-typed seed is not a seed. While the text
   * does not parse, `seed` keeps its last good value, the screen keeps working,
   * and a note says which seed is actually in force. Clearing the field to
   * retype it is not an error and is not reported as one.
   */
  const [seedText, setSeedText] = useState(String(initial.seed));
  const [fieldSize, setFieldSize] = useState(initial.fieldSize);
  const [rosterSize, setRosterSize] = useState(initial.rosterSize);
  /**
   * D-018 — whose dice this race is run on. Deliberately NOT seeded from
   * `initial`: it is a per-race decision, not a remembered setting. See the
   * note by the checkbox.
   */
  const [handRolled, setHandRolled] = useState(false);
  /** D-EZ-103 — this race's realism house rules; on by default, under Advanced. */
  const [houseRules, setHouseRules] = useState<HouseRuleOptions>(() => ({ ...(initial.houseRules ?? {}) }));
  const [useCardLength, setUseCardLength] = useState(true);
  const [customLength, setCustomLength] = useState(initial.totalTurns);

  const [week, setWeek] = useState<RaceWeekResult | null>(null);
  const [weekSeed, setWeekSeed] = useState<number | null>(null);
  /**
   * Was the week on screen qualified for a ROAD COURSE? Null before one is run.
   *
   * The week needs exactly one fact about the track — EZRA-S2 §4.0 RC, the only
   * trait whose effect lands during qualifying — so this, and not the track id,
   * is what can make a week stale when the race moves.
   */
  const [weekRoadCourse, setWeekRoadCourse] = useState<boolean | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [pace, setPace] = useState<Pace>('stepped');
  const [cursor, setCursor] = useState(0);
  const [playing, setPlaying] = useState(false);
  /** UI-008 — whose dossier is open over the starting grid. */
  const [dossier, setDossier] = useState<string | null>(null);

  // Someone who has asked their machine not to animate things does not want a
  // week that arrives one beat at a time either: they get the whole page at
  // once, and can still choose to step through it.
  useEffect(() => {
    try {
      if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) setPace('instant');
    } catch {
      /* no matchMedia — the stepped default stands. */
    }
  }, []);

  // Moving between the three screens is moving between three documents; the
  // reader starts each one at the top. Jumped, not scrolled — a smooth scroll
  // is motion nobody asked for.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [stage]);

  /**
   * THE CALENDAR — 36 named races, built from this seed, in season mode only.
   *
   * One seed, one year: the schedule and every die in the season come off the
   * same number, which is what makes a season reproducible from it.
   */

  /**
   * WHO CHOSE THIS TRACK, and therefore what the controls below are allowed to
   * do. The whole decision is `planEntry`, and it is pure and tested — this
   * screen only draws it.
   */
  const plan = useMemo(
    () =>
      planEntry(
        { mode, seed, pickedTrackId: trackId, useCardLength, customLength },
      ),
    [mode, seed, trackId, useCardLength, customLength],
  );

  const track = useMemo(() => trackCard(plan.trackId), [plan.trackId]);
  const totalTurns = plan.totalTurns;
  const roadCourse = track.category === 'Road Course';
  const groups = useMemo(() => byCategory(TRACK_CARDS), []);
  const seedReading = useMemo(() => parseSeed(seedText), [seedText]);

  /**
   * Take a typed seed the moment it is a seed. Nothing else in the screen has
   * to know the field is text: `seed` is only ever set to a number that parsed.
   */
  const onSeedText = useCallback((text: string) => {
    setSeedText(text);
    const { seed: parsed } = parseSeed(text);
    if (parsed !== null) setSeed(parsed);
  }, []);

  /** Howard: *"a seed randomizer button instead of increments."* */
  const rollSeed = useCallback(() => {
    const next = randomSeed();
    setSeed(next);
    setSeedText(String(next));
  }, []);

  // ---- the garage ---------------------------------------------------------
  const field = useMemo(() => {
    try {
      /*
        A SCENARIO DEALS NO CARDS — D-EZ-76. Its garage is the printed one, so
        the roster size control is not merely ignored here, it is removed from
        the screen below. Everything downstream — the dossier, the liveries,
        race week — takes the same shape either way, because the only thing
        that changed is where the cards came from.
      */
      const built = buildField(seed, { size: rosterSize });
      return { drivers: built.drivers, teams: built.teams, error: null as string | null };
    } catch (e) {
      return { drivers: [], teams: [] as Team[], error: String((e as Error)?.message ?? e) };
    }
  }, [seed, rosterSize]);

  const cards = useMemo(
    () => new Map<string, DriverCard>(field.drivers.map((d) => [d.card.driverId, d.card])),
    [field.drivers],
  );

  const teamNames = useMemo(
    () => new Map<string, string>(field.teams.map((t) => [t.teamId, t.name])),
    [field.teams],
  );

  const liveries = useMemo(() => {
    const seats = new Map<string, { team: Team; index: number }>();
    for (const team of field.teams) {
      team.driverIds.forEach((id, index) => seats.set(id, { team, index }));
    }
    const map = new Map<string, Livery>();
    for (const driver of field.drivers) {
      const seat = seats.get(driver.card.driverId);
      map.set(
        driver.card.driverId,
        liveryForDriver(driver.card.driverId, driver.card.teamId, seat?.index, seat?.team.baseInk, driver.card.make),
      );
    }
    return map;
  }, [field.drivers, field.teams]);

  // ---- the week ------------------------------------------------------------
  const story = useMemo(
    () => (week ? weekStory(week, field.drivers.length) : null),
    [week, field.drivers.length],
  );

  /** D-EZ-56 — this week's ledger is the whole history a one-off race has. */
  const feudHistory = useMemo(
    () => (week ? feudHistories(week.feuds, { week: week.events }) : undefined),
    [week],
  );

  const beatCount = story?.beats.length ?? 0;
  const revealed = pace === 'instant' ? beatCount : Math.min(cursor, beatCount);
  const complete = revealed >= beatCount;

  const runWeek = useCallback(() => {
    try {
      const roster = field.drivers.map((d) => d.card);
      /**
       * `roadCourse` IS PASSED NOW, AND WAS NOT BEFORE — a real bug, found
       * while wiring the schedule in.
       *
       * `runRaceWeek` takes one fact about the track (EZRA-S2 §4.0 RC, the Road
       * Course Expert's qualifying effect) and `season.ts` has always handed it
       * down. This screen never did, so every week set up here — including one
       * at Sonoma or Watkins Glen — qualified as though the race were on an
       * oval, and the RC trait did nothing. Weeks rolled at a road course
       * before this change therefore differ from the same seed after it; weeks
       * at every other track are byte-for-byte identical, because the flag is
       * read nowhere else.
       */
      const result = runRaceWeek({ roster, seed, options: { fieldSize, roadCourse } });
      setWeek(result);
      setWeekSeed(seed);
      setWeekRoadCourse(roadCourse);
      setCursor(0);
      setPlaying(true);
      setError(null);
      setStage('week');
    } catch (e) {
      setError(String((e as Error)?.message ?? e));
    }
  }, [field.drivers, seed, fieldSize, roadCourse]);

  // D-EZ-103 — "Run another race here": straight into race week, once.
  const autoRan = useRef(false);
  useEffect(() => {
    if (!startAtWeek || autoRan.current) return;
    autoRan.current = true;
    runWeek();
  }, [startAtWeek, runWeek]);

  // The tape. One beat, then the next, until the week has been told.
  useEffect(() => {
    if (!story || pace !== 'stepped' || !playing) return;
    if (cursor >= story.beats.length) {
      setPlaying(false);
      return;
    }
    const timer = window.setTimeout(() => setCursor((c) => c + 1), BEAT_MS);
    return () => window.clearTimeout(timer);
  }, [story, pace, playing, cursor]);

  const toTheGrid = useCallback(() => {
    setPlaying(false);
    setCursor(beatCount);
    setStage('grid');
  }, [beatCount]);

  /**
   * WHAT, IF ANYTHING, HAS MOVED UNDER THE WEEK ON SCREEN.
   *
   * Two things can, and they are listed separately because they are different
   * facts and the fix is the same but the reason is not. A track change on its
   * own is NOT staleness: race week deals cards, and the only thing it knows
   * about the track is whether it is a road course.
   */
  const staleReasons: string[] = [];
  if (week !== null && weekSeed !== seed) {
    staleReasons.push(
      citations ? `Rolled off seed ${weekSeed}; the seed is now ${seed}.` : 'The replay number has changed since this week was rolled.',
    );
  }
  if (week !== null && weekRoadCourse !== null && weekRoadCourse !== roadCourse) {
    // The citation stays — a rule reference is not an over-explainer — but the
    // sentence explaining what a citation is does not.
    staleReasons.push(
      weekRoadCourse
        ? `Qualified for a road course, racing on an oval${citations ? ' (RC trait, EZRA-S2 §4.0)' : ''}.`
        : `Qualified for an oval, racing on a road course${citations ? ' (RC trait, EZRA-S2 §4.0)' : ''}.`,
    );
  }
  const stale = staleReasons.length > 0;

  // D-026 — the crest's accent colour tints the masthead band.
  const accent = useRaceAccent(plan.raceKey, track.id);

  return (
    <div className="rw">
      <header className="rw__masthead" {...raceAccentProps(accent)}>
        <p className="kicker">Red White &amp; Blue Racin&rsquo; · exhibition · one race</p>
        <h1 className="rw__mastname">
          <RaceLogo raceKey={plan.raceKey} size={56} />
          {plan.raceName}
        </h1>
        <p className="rw__dek">
          {track.name}
          {track.location ? ` · ${track.location}` : ''} · {totalTurns} turns
          {/* D-EZ-101 — the replay number is not a headline. */}
          <Cite> · seed {seed}</Cite>
          {' · '}
          {field.drivers.length} licensed, {Math.min(fieldSize, field.drivers.length)} will start
        </p>
      </header>

      <nav className="rw__stages" aria-label="Race week stages">
        {(
          [
            ['entry', 'Entry list'],
            ['week', 'Race week'],
            ['grid', 'Starting grid'],
          ] as [Stage, string][]
        ).map(([id, label]) => (
          <button
            key={id}
            type="button"
            className={`rwstep${stage === id ? ' is-on' : ''}`}
            aria-current={stage === id ? 'step' : undefined}
            disabled={id !== 'entry' && !week}
            onClick={() => setStage(id)}
          >
            {label}
          </button>
        ))}
      </nav>

      {(error || field.error) && (
        <p className="err" role="alert">
          {error ?? field.error}
        </p>
      )}

      {/* ================================================== 1 · ENTRY LIST == */}
      {stage === 'entry' && (
        <div className="rw__entry">
          <form
            className="setup__form"
            onSubmit={(e) => {
              e.preventDefault();
              runWeek();
            }}
          >
            {note}
            <div className="field">
              <span className="field__legend">An exhibition</span>
              <p className="field__note">
                One race at the track of your choosing. Nothing counts toward a championship.
              </p>
              {onBack && (
                <button type="button" className="btn btn--quiet rw__back" onClick={onBack}>
                  Back
                </button>
              )}
            </div>

            {/* THE TRACK — the player's choice in an exhibition. */}
            <div className="field">
              <label htmlFor="setup-track">Track</label>
              <select id="setup-track" value={trackId} onChange={(e) => setTrackId(e.target.value)}>
                {groups.map(([category, trackCards]) => (
                  <optgroup key={category} label={category}>
                    {trackCards.map((card) => (
                      <option key={card.id} value={card.id}>
                        {card.name} — {card.turns} turns
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
              <p className="field__note">
                {track.raceName ?? track.name}
                {track.location ? ` · ${track.location}` : ''}
                <Cite>
                  {' '}· escape {track.escapeAdj} · repair {track.repairAdj}
                </Cite>
              </p>
            </div>

            <div className="field">
              <span className="field__legend" id="setup-length-label">
                Distance
              </span>
              <div className="segmented" role="group" aria-labelledby="setup-length-label">
                <button
                  type="button"
                  className={`seg${useCardLength ? ' is-on' : ''}`}
                  aria-pressed={useCardLength}
                  onClick={() => setUseCardLength(true)}
                >
                  Card distance · {track.turns} turns
                </button>
                <button
                  type="button"
                  className={`seg${!useCardLength ? ' is-on' : ''}`}
                  aria-pressed={!useCardLength}
                  onClick={() => setUseCardLength(false)}
                >
                  Set my own
                </button>
              </div>
              {!useCardLength && (
                <select
                  aria-label="Race length in turns"
                  value={customLength}
                  onChange={(e) => setCustomLength(Number(e.target.value))}
                >
                  {LENGTHS.map((n) => (
                    <option key={n} value={n}>
                      {n} turns · {n * 10} miles
                    </option>
                  ))}
                </select>
              )}
              <p className="field__note">
                One turn is ten miles<Cite> (RB/ii)</Cite>. {totalTurns} turns = {totalTurns * 10} miles
                {track.lengthMiles ? ` ≈ ${Math.round((totalTurns * 10) / track.lengthMiles)} laps` : ''}.
              </p>
            </div>

            <div className="field">
              <label htmlFor="setup-roster">Drivers with a licence</label>
              <select id="setup-roster" value={rosterSize} onChange={(e) => setRosterSize(Number(e.target.value))}>
                {ROSTER_SIZES.map((n) => (
                  <option key={n} value={n}>
                    {n} drivers{n === DEFAULT_ROSTER_SIZE ? ' · full garage' : ''}
                  </option>
                ))}
              </select>
              <p className="field__note">Everyone who turns up to try. Not the field — race week decides that.</p>
            </div>

            <div className="field">
              <label htmlFor="setup-size">Cars that start</label>
              <select id="setup-size" value={fieldSize} onChange={(e) => setFieldSize(Number(e.target.value))}>
                {FIELD_SIZES.map((n) => (
                  <option key={n} value={n}>
                    {n} cars{n === MAX_STARTING_FIELD ? ' · full field' : ''}
                  </option>
                ))}
              </select>
              <p className="field__note">
                Race week fills the field to this size from the bubble<Cite> (RWEG/p.4)</Cite>.{' '}
                {Math.max(0, field.drivers.length - fieldSize)} go home.
              </p>
            </div>

            {/*
              D-EZ-101 / D-EZ-103 — ADVANCED: the house rules and the replay
              number. The house rules are on by default, the same as a new
              league's; the replay number is how a race is played again exactly.
            */}
            <details className="advanced">
              <summary>Advanced — house rules and replay number</summary>
              <p className="field__note">This race: {settingsSummary({ race: houseRules, sheet: {} })}.</p>
              <RealismRules value={houseRules} onChange={setHouseRules} idPrefix="ex-race" />
              <div className="field field--row">
                <div>
                  <label htmlFor="setup-seed">Replay number</label>
                  <input
                    id="setup-seed"
                    type="text"
                    inputMode="numeric"
                    autoComplete="off"
                    spellCheck={false}
                    value={seedText}
                    onChange={(e) => onSeedText(e.target.value)}
                  />
                </div>
                <button type="button" className="btn btn--quiet" onClick={rollSeed}>
                  🎲 New number
                </button>
              </div>
              <p className="field__note">
                {seedReading.problem ? (
                  <b className="warn">
                    {seedReading.problem} Still using {seed}.
                  </b>
                ) : seedReading.seed === null ? (
                  <>Type or paste a number to play a race again. Still using {seed}.</>
                ) : (
                  <>The same number, track and distance is the same race.</>
                )}
              </p>
            </details>

            <button type="submit" className="btn btn--hot btn--big" disabled={field.drivers.length === 0}>
              Run race week
            </button>
            {stale && (
              <p className="field__note">
                {staleReasons.join(' ')} Run race week again to roll it for this race.
              </p>
            )}
          </form>

          {/* One child of `.rw__entry`'s two-column grid, so the cards stack in
              the right-hand column instead of under the form. */}
          <div className="rw__entrymain">
            <section className="rwentry" aria-labelledby="rwentry-title">
              <header className="rwsec__head">
                <p className="kicker">The entry list</p>
                <h2 id="rwentry-title">{field.drivers.length} licensed drivers</h2>
                <p className="rwsec__sub">
                  {field.drivers.length} cards dealt into four stacks;{' '}
                  <b>{Math.min(fieldSize, field.drivers.length)} start</b>.
                </p>
              </header>
              <ol className="rwentry__cards">
                {field.drivers.map((driver) => (
                  <li key={driver.card.driverId}>
                    <DriverTile
                      card={driver.card}
                      livery={liveries.get(driver.card.driverId)!}
                      teamName={teamNames.get(driver.card.teamId ?? '')}
                      compact
                    />
                  </li>
                ))}
              </ol>
            </section>
          </div>
        </div>
      )}

      {/* =================================================== 2 · RACE WEEK == */}
      {stage === 'week' && story && (
        <div className="rw__week">
          <div className="rwbar">
            <div className="rwbar__pace">
              <span className="pace__label" id="rw-pace-label">
                Reveal
              </span>
              <div className="segmented segmented--tiny" role="group" aria-labelledby="rw-pace-label">
                <button
                  type="button"
                  className={`seg${pace === 'stepped' ? ' is-on' : ''}`}
                  aria-pressed={pace === 'stepped'}
                  onClick={() => setPace('stepped')}
                >
                  Stepped
                </button>
                <button
                  type="button"
                  className={`seg${pace === 'instant' ? ' is-on' : ''}`}
                  aria-pressed={pace === 'instant'}
                  onClick={() => {
                    setPace('instant');
                    setCursor(beatCount);
                    setPlaying(false);
                  }}
                >
                  Instant
                </button>
              </div>
            </div>

            {pace === 'stepped' && (
              <div className="rwbar__controls">
                <button
                  type="button"
                  className="btn btn--quiet"
                  onClick={() => setPlaying((p) => !p)}
                  disabled={complete}
                >
                  {playing ? 'Hold' : 'Play'}
                </button>
                <button
                  type="button"
                  className="btn btn--quiet"
                  onClick={() => {
                    setPlaying(false);
                    setCursor((c) => Math.min(beatCount, c + 1));
                  }}
                  disabled={complete}
                >
                  Next beat
                </button>
              </div>
            )}

            <p className="rwbar__count" role="status" aria-live="polite">
              {complete
                ? `The week is told — ${story.starters} cars in the field.`
                : `${revealed} of ${beatCount} so far`}
            </p>

            <button type="button" className="btn btn--hot" onClick={toTheGrid}>
              {complete ? 'See the starting grid' : 'Skip to the grid'}
            </button>
          </div>

          {/*
            D-EZ-56 — the chevron on each feud row. A single race has NO season
            behind it, so the only history that exists is the week just rolled,
            and that is exactly what is passed. It is not padded out to look
            like a career: two men who fell out ten minutes ago get one row and
            the summary says so.
          */}
          <WeekReveal
            story={story}
            cards={cards}
            teamNames={teamNames}
            cursor={revealed}
            feudHistory={feudHistory}
          />

          {complete && (
            <div className="rwhandoff reveal">
              <p className="kicker">Step six</p>
              <h2>The field is set</h2>
              {story.gridLine && <p className="rwhandoff__line">{story.gridLine.line}</p>}
              <button type="button" className="btn btn--hot btn--big" onClick={toTheGrid}>
                See the starting grid
              </button>
              {story.gridLine && <Cite as="p" className="cite">{story.gridLine.citation}</Cite>}
            </div>
          )}
        </div>
      )}

      {/* ================================================== 3 · THE GRID ==== */}
      {stage === 'grid' && story && week && (
        <div className="rw__grid">
          <StartingGrid
            rows={story.grid}
            dnq={story.dnq}
            bubble={story.bubbleUnused}
            cards={cards}
            liveries={liveries}
            teamNames={teamNames}
            rosterSize={story.rosterSize}
            onSelectDriver={setDossier}
          />

          {/* UI-008 — the same modal the boards and the career screen open,
              built from the week rather than from a tape, because on this
              screen the race has not been run yet. */}
          {dossier && (
            <DriverDossier
              driverId={dossier}
              week={{
                // The EVENT's name, which in season mode is the calendar's and
                // not the card's — see `RaceConfig.raceName`.
                raceName: plan.raceName,
                row: story.grid.find((r) => r.card.driverId === dossier),
                standing: story.grid.some((r) => r.card.driverId === dossier)
                  ? 'grid'
                  : story.dnq.includes(dossier)
                    ? 'dnq'
                    : 'bubble',
                cards,
                // Grid order, then the ones who did not make the show — so ‹ ›
                // walks the field in the order this screen lists it.
                order: [...story.grid.map((r) => r.card.driverId), ...story.dnq],
                teams: field.teams,
              }}
              onClose={() => setDossier(null)}
              onSelectDriver={setDossier}
            />
          )}

          <div className="rwhandoff rwhandoff--green">
            <p className="kicker">Green flag</p>
            <h2>Take the green</h2>
            <p className="rwhandoff__line">
              {Object.keys(week.tvList).length} on the TV list · {week.feuds.length} feud
              {week.feuds.length === 1 ? '' : 's'} · {week.fuming.length} fuming ·{' '}
              {Object.values(week.chips).reduce((n, c) => n + c, 0)} chips in hand.
            </p>
            {/*
              D-018 — WHOSE DICE. Offered here, at the green, because it is a
              decision about this race and not a stored preference: a 50-turn
              race wants roughly 900 faces, and nobody should discover that
              because a checkbox was still ticked from last time. It is not
              pre-selected and it does not persist.
            */}
            <label className="rwhandoff__dice">
              <input
                type="checkbox"
                checked={handRolled}
                onChange={(e) => setHandRolled(e.target.checked)}
              />{' '}
              <strong>Roll my own dice.</strong>{' '}
              <span className="dim">~900 throws. You can hand the rest back at any point.</span>
            </label>
            <div className="rwhandoff__row">
              <button
                type="button"
                className="btn btn--green btn--big"
                onClick={() =>
                  onStart(
                    {
                      // `plan`, not the raw controls: in season mode the track
                      // and the distance belong to the calendar and the state
                      // behind the (hidden) select is not what is being raced.
                      trackId: plan.trackId,
                      totalTurns: plan.totalTurns,
                      seed,
                      fieldSize,
                      rosterSize,
                      raceName: plan.raceName,
                      handRolled,
                      houseRules,
                    },
                    week,
                  )
                }
              >
                {handRolled ? 'Drop the green — your dice' : 'Drop the green'}
              </button>
              <button type="button" className="btn btn--quiet" onClick={() => setStage('week')}>
                Back to the week
              </button>
              <button
                type="button"
                className="btn btn--quiet"
                onClick={() => {
                  setStage('entry');
                  setWeek(null);
                }}
              >
                Another week
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
